import { U as Url } from './url.min.js';
import { t as translateDate } from './translate-date.js';
import { g as getRoomType } from './get-room-type.js';
import './index2.js';

/**
 * **Centrotel**
 *
 * @exports bs_89
 * @category engines
 */

function factory({ window, document, thn }) {

  const engine = {};

  /**
     * @memberOf module:bs_89
     * @see {@link language_check}
     *
     * @param {object} ctx - The context object.
     */
  engine.languageCheck = function languageCheck(ctx) {

    return new Promise(function (resolve, reject) {

      thn.domReady().then(function () {
        const isBookingEngine = !!window.location.hostname.match(/www.centrotel.es/);
        let langEl;

        if (!isBookingEngine) {
          const lang = window.location.pathname.match(/[a-z]+/);
          langEl = (lang && lang[0]) || 'es';

          if (langEl && ctx.langs.test(langEl)) {
            resolve(langEl);
            return;
          }
        }

        // Check the html lang tag
        langEl = document.documentElement.lang;
        const htmlLang = langEl && langEl.substr(0, 2)?.toLowerCase();
        if (htmlLang && ctx.langs.test(htmlLang)) {
          resolve(htmlLang);
        }
      });
    });
  };

  /**
     * @memberOf module:bs_89
     * @see {@link generate_search_url}
     *
     * @param {object} ctx - The context object.
     */
  engine.generateSearchUrl = function generateSearchUrl(ctx) {

    const url = new Url(ctx.baseUrl);
    const langs = {
      en: 'en-GB',
      es: 'es-ES',
    };

    // https://www.centrotel.es/newCRSUI2/booking/itemsSelection.aspx?CRSName=gruparbo&idLanguage=es-ES&idEstablishmentSRT=2&idPT=14%2c18&idP=107%2c108%2c109%2c106%2c233%2c239%2c250%2c226%2c254%2c255%2c256%2c257%2c292%2c285&start=13%2f11%2f2020&end=14%2f11%2f2020&promo=MATCH5&head=0&head2=1&calendar=1&flags=1
    if (url.host.match(/www\.centrotel\.es$/)) {

      // prepare URL for params substitution
      url.query.start = '[date_begin]';
      url.query.end = '[date_end]';
      url.query.idLanguage = langs[thn.data.language];

      // export into ctx.new_url
      ctx.newUrl = url.decode(url.toString());
    } else {
      // This is not a valid BE URL
      ctx.newUrl = '';
    }

    return ctx;
  };

  /**
     * @memberOf module:bs_89
     * @see {@link Price#get_promocode}
     *
     * @param {object} ctx - The context object.
     */
  engine.getPromocode = function getPromocode(ctx) {
    const promocode = document.querySelector('#co_promocion')?.value;

    if (promocode) {
      ctx.promocode = promocode;
    }

    return ctx.promocode;
  };

  /**
    *
    * @memberOf module:bs_89
    *
    */

  const roomDetailsParseData = () => {
    const roomsArray = [...document.querySelectorAll('.co-habitacion-container')];

    if (thn.empty(roomsArray)) {
      return {};
    }

    const roomsInfo = roomsArray
      .map((room) => {
        const roomName = room.querySelector('[id*=lblTipoProductoNombre]')?.textContent;
        const roomType = getRoomType(roomName);
        const adults = thn.parseNumber(room.querySelector('.co-fila-ocupacion .co-oculto-moviles')?.textContent);
        const ratesInfo = [...room.querySelectorAll('.co-fila-selecciones:not(.co-fila-selecciones-cabecera)')]
          ?.map((rate) => {
            const rateName = rate.querySelector('.co-tarifa-encabezado')?.textContent;
            const ratePrice = thn.parseNumber(rate.querySelector('.co-fila-total-precios .co-destacado')?.textContent);
            const isRefundable = true;
            const hasBreakfast = !!rateName.match(/breakfast|desayuno/i);
            const rateData = {
              id: '',
              name: rateName,
              totalNight: ratePrice,
              breakfast: hasBreakfast,
              refundable: isRefundable,
            };

            return rateData;
          });

        const roomData = {
          id: '',
          name: roomName,
          adults: adults || 2,
          type: roomType,
          children: 0,

          prices: ratesInfo,
        };

        return roomData;
      });

    return roomsInfo;
  };

  /**
     * @memberOf module:bs_89
     * @this thn.widgets.price
     * @see {@link Price#set_data}
     *
     * @param {object} ctx - The context object.
     */
  engine.priceParseData = function priceParseData(ctx) {
    const widgetData = {};

    let nights;

    const dateBeginDom = translateDate(document.querySelector('#co_js_selector_fecha_entrada')?.textContent, thn.data.language);
    const dateEndDom = translateDate(document.querySelector('#co_js_selector_fecha_salida')?.textContent, thn.data.language);
    const dateBeginParam = thn.parseUrlParameter('start')?.replace(/(\d+)\D+(\d+)\D+(\d+)/, '$3/$2/$1');
    const dateEndParam = thn.parseUrlParameter('end')?.replace(/(\d+)\D+(\d+)\D+(\d+)/, '$3/$2/$1');
    const dateBegin = dateBeginDom || dateBeginParam;
    const dateEnd = dateEndDom || dateEndParam;

    if (dateBegin && dateEnd) {
      widgetData.dateBegin = dateBegin;
      widgetData.dateEnd = dateEnd;
      nights = thn.daysBetween(widgetData.dateBegin, widgetData.dateEnd) || thn.parseNumber(document.querySelector('#co_js_noches')?.textContent);
    }

    const rates = document.querySelectorAll('.co-fila-selecciones:not(.co-fila-selecciones-cabecera)');
    const priceSelector = '.co-fila-total-precios .co-destacado';
    const prices = [...rates].map((rate) => thn.parseNumber(rate.querySelector(priceSelector)?.textContent)).filter(Boolean);
    const currency = thn.parseCurrency(document.querySelector(priceSelector)?.textContent);
    const price = !thn.empty(prices) && Math.min(...prices);

    const rateWithLowestPrice = !thn.empty(rates) && rates[prices.indexOf(price)];

    const adults = (rateWithLowestPrice && thn.parseNumber(rateWithLowestPrice.querySelector('.co-fila-ocupacion .co-oculto-moviles')?.textContent)) || 2;
    widgetData.adults = adults;
    widgetData.children = 0;

    const noAvailability = !!thn.empty(rates) && nights;

    if (nights && price) {
      widgetData.price = price / nights;
      widgetData.currency = currency;
      widgetData.roomDetails = roomDetailsParseData();
      widgetData.set = true;
    } if (noAvailability) {
      widgetData.noRooms = true;
    }

    return widgetData;
  };

  /**
     * @memberOf module:bs_89
     * @this thn.widgets.booking
     * @see {@link Booking#set_data}
     *
     * @param {object} ctx - The context object.
     */
  engine.bookingParseData = function bookingParseData(ctx) {
    const widgetData = {};

    const bookingId = thn.parseNumber(document.querySelector('#ctl00_phContenido_txtLocalizador')?.textContent)?.toString() || document.querySelector('#ctl00_phContenido_txtLocalizador2')?.textContent;

    if (!bookingId) {
      widgetData.set = false;
      return widgetData;
    }

    widgetData.bookingId = bookingId;
    widgetData.dateBegin = document.querySelector('#ctl00_phContenido_txtEntrada')?.textContent?.replace(/\D*(\d+)\D+(\d+)\D+(\d+)\D*/, '$3/$2/$1');
    widgetData.dateEnd = document.querySelector('#ctl00_phContenido_txtSalida')?.textContent?.replace(/\D*(\d+)\D+(\d+)\D+(\d+)\D*/, '$3/$2/$1');

    const priceText = document.querySelector('#ctl00_phContenido_txtPrecio')?.textContent;
    widgetData.price = thn.parseNumber(priceText);
    widgetData.currency = thn.parseCurrency(priceText) || 'EUR';

    return widgetData;
  };

  return engine;
}

export { factory };
